import React, { useState } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { MapPin, Briefcase, CheckCircle, Video, Search, Star } from 'lucide-react';
import { DemoBanner } from '../../components/DemoBanner';
import { candidates } from '../../mockData';
import { Link } from 'react-router-dom';

const CandidateMatches = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');

  const filteredCandidates = candidates.filter(candidate => {
    const matchesSearch = candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         candidate.profession.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         candidate.specialisation.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || 
                         (filter === 'verified' && candidate.status === 'verified') ||
                         (filter === 'pending' && candidate.status === 'pending-review');
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Candidate Matches</h1>
            <p className="text-gray-600">Browse candidates matched to your positions</p>
          </div>

          {/* Demo Banner */}
          <DemoBanner 
            type="demo"
            title="Sample Candidate Profiles"
            description="The candidates shown below are sample profiles for demonstration. View real candidates in the Talent Pool section, where you can request to connect with actual medical professionals."
          />

          {/* Filters */}
          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Search by name, profession, or specialisation..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Candidates</SelectItem>
                      <SelectItem value="verified">Verified Only</SelectItem>
                      <SelectItem value="pending">Pending Review</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Tabs defaultValue="matches" className="mb-8">
            <TabsList>
              <TabsTrigger value="matches">Top Matches ({filteredCandidates.length})</TabsTrigger>
              <TabsTrigger value="saved">Saved (0)</TabsTrigger>
              <TabsTrigger value="interviewed">Interviewed (0)</TabsTrigger>
            </TabsList>

            <TabsContent value="matches" className="mt-6">
              <div className="space-y-6">
                {filteredCandidates.map((candidate) => (
                  <Card key={candidate.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-6">
                        {/* Avatar */}
                        <img 
                          src={candidate.avatar} 
                          alt={candidate.name} 
                          className="w-20 h-20 rounded-full flex-shrink-0"
                        />

                        {/* Main Content */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h3 className="text-xl font-semibold text-gray-900 mb-1">{candidate.name}</h3>
                              <p className="text-gray-600">{candidate.profession} • {candidate.specialisation}</p>
                              <div className="flex items-center space-x-4 text-sm text-gray-500 mt-2">
                                <div className="flex items-center space-x-1">
                                  <MapPin className="h-4 w-4" />
                                  <span>{candidate.country}</span>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <Briefcase className="h-4 w-4" />
                                  <span>{candidate.experience} years experience</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Badge className="bg-[#42b1c2] hover:bg-[#3a9fb0]">
                                AI Score: {candidate.aiScore}
                              </Badge>
                              {candidate.status === 'verified' && (
                                <Badge className="bg-green-100 text-green-800">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Verified
                                </Badge>
                              )}
                            </div>
                          </div>

                          {/* Skills */}
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-900 mb-2">Key Skills:</h4>
                            <div className="flex flex-wrap gap-2">
                              {candidate.skills.map((skill, index) => (
                                <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Status Indicators */}
                          <div className="flex items-center space-x-4 mb-4 text-sm">
                            {candidate.videoStatus === 'completed' && (
                              <div className="flex items-center space-x-1 text-green-600">
                                <Video className="h-4 w-4" />
                                <span>Video interview available</span>
                              </div>
                            )}
                            {candidate.documentStatus === 'verified' && (
                              <div className="flex items-center space-x-1 text-green-600">
                                <CheckCircle className="h-4 w-4" />
                                <span>Documents verified</span>
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center space-x-3 pt-4 border-t">
                            <Link to={`/employer/candidates/${candidate.id}`}>
                              <Button className="bg-[#42b1c2] hover:bg-[#3a9fb0] text-white">
                                View Full Profile
                              </Button>
                            </Link>
                            <Button variant="outline">
                              <Video className="h-4 w-4 mr-2" />
                              Watch Video
                            </Button>
                            <Button variant="outline">
                              Request Interview
                            </Button>
                            <Button variant="ghost" size="sm">
                              <Star className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {filteredCandidates.length === 0 && (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <Search className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">No candidates found</h3>
                      <p className="text-gray-600">Try adjusting your search criteria</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="saved">
              <Card>
                <CardContent className="p-12 text-center">
                  <Star className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No saved candidates yet</h3>
                  <p className="text-gray-600">Star candidates to save them for later review</p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="interviewed">
              <Card>
                <CardContent className="p-12 text-center">
                  <Video className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No interviews conducted yet</h3>
                  <p className="text-gray-600">Candidates you've interviewed will appear here</p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CandidateMatches;